import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { useGetPaginatedInvestigations } from '@/application/services/public-investigation.service'
import { classNames } from '@/ui/utils/main'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/common/Input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components/common/Select'
import { CategoryFilterSelect } from '@/ui/components/forms/CategoryFilterSelect'
import { InvestigationCard } from './InvestigationCard'
import { CardSkeleton } from './CardSkeleton'
import { SuggestionsSection } from './SuggestionsSection'

const LIMIT = 10

/**
 * Investigations browsing block: status tabs (active/inactive/suggestions), search +
 * sort filters, paginated list of investigation cards. Owns all related state and
 * the data fetching for the paginated investigations.
 * The "suggestions" tab renders the community suggestions panel inline.
 *
 * The category filter is fed by the category taxonomy and sends the term slug
 * as the `category` query param.
 */
export function InvestigationsListSection() {
  const { t } = useTranslation()

  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string | undefined>(undefined)
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'category'>('date')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [page, setPage] = useState(1)
  const [statusTab, setStatusTab] = useState<'active' | 'inactive' | 'suggestions'>('active')

  const { data, isLoading } = useGetPaginatedInvestigations({
    page,
    limit: LIMIT,
    search: search || undefined,
    category,
    sortBy,
    sortOrder,
    status: statusTab !== 'suggestions' ? statusTab : 'active',
  })

  const totalPages = data ? Math.ceil(data.total / LIMIT) : 0

  return (
    <>
      {/* Status tabs */}
      <div className="flex items-center gap-2 flex-wrap mb-6">
        {(['active', 'inactive', 'suggestions'] as const).map((status) => (
          <button
            key={status}
            onClick={() => {
              setStatusTab(status)
              setPage(1)
            }}
            className={classNames(
              'px-3 py-1 rounded-full text-sm font-medium transition-colors border',
              statusTab === status
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground',
            )}
          >
            {status === 'active'
              ? t('investigations.tab_active')
              : status === 'inactive'
                ? t('investigations.tab_inactive')
                : t('investigations.suggestions_title')}
          </button>
        ))}
      </div>

      {/* Suggestions tab content */}
      {statusTab === 'suggestions' ? (
        <SuggestionsSection />
      ) : (
        <>
          {/* Filter row */}
          <div className="flex flex-col md:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-9"
                placeholder={t('investigations.search_placeholder')}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value)
                  setPage(1)
                }}
              />
            </div>

            <CategoryFilterSelect
              className="w-full md:w-52"
              value={category}
              onChange={(slug) => {
                setCategory(slug)
                setPage(1)
              }}
            />

            <Select
              value={sortBy}
              onValueChange={(value) => {
                if (value === 'date' || value === 'name' || value === 'category') setSortBy(value)
              }}
            >
              <SelectTrigger className="w-full md:w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">{t('investigations.sort_date')}</SelectItem>
                <SelectItem value="name">{t('investigations.sort_title')}</SelectItem>
                <SelectItem value="category">{t('investigations.sort_category')}</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'))}
              className="shrink-0 self-start md:self-auto"
            >
              {sortOrder === 'DESC' ? '↓' : '↑'}
            </Button>
          </div>

          {/* Investigation list */}
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: LIMIT }).map((_item, index) => (
                <CardSkeleton key={index} />
              ))}
            </div>
          ) : !data || data.investigations.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground border border-border rounded-lg">
              <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p>{t('investigations.no_results')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.investigations.map((investigation) => (
                <InvestigationCard key={investigation.id} investigation={investigation} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {data && data.total > LIMIT && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
              >
                ←
              </Button>

              {Array.from({ length: totalPages }, (_item, index) => index + 1)
                .filter(
                  (pageNum) =>
                    pageNum === 1 || pageNum === totalPages || Math.abs(pageNum - page) <= 2,
                )
                .map((pageNum, index, pageArray) => (
                  <React.Fragment key={pageNum}>
                    {index > 0 && pageArray[index - 1] !== pageNum - 1 && (
                      <span className="text-muted-foreground px-1">…</span>
                    )}
                    <Button
                      variant={pageNum === page ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  </React.Fragment>
                ))}

              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={!data || page >= totalPages}
              >
                →
              </Button>
            </div>
          )}
        </>
      )}
    </>
  )
}
